/**
 * Finding something that was said, and keeping something worth finding again.
 *
 * Two halves of the same problem. Search narrows by who said it, what it
 * carries and when, because "the map somebody posted last week" is a real
 * question and scrolling is not an answer. Saved is your own shelf: pinning
 * is for the room, this is for you, and nobody else ever sees it.
 */

import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Message } from '@/lib/types';
import { cn } from '@/lib/cn';
import { go } from '@/lib/hash';
import { ago } from '@/lib/notifications';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { Avatar } from './Avatar';
import { EmojiText } from './EmojiText';
import { Button, Modal, useToast } from './ui';

type Tab = 'search' | 'saved';
type Has = '' | 'image' | 'link' | 'file' | 'voice';

const HAS: { id: Has; label: string; icon: string }[] = [
  { id: '', label: 'Anything', icon: 'search' },
  { id: 'image', label: 'Pictures', icon: 'image' },
  { id: 'link', label: 'Links', icon: 'link' },
  { id: 'file', label: 'Files', icon: 'paperclip' },
  { id: 'voice', label: 'Voice notes', icon: 'mic' }
];

const WHEN = [
  { id: '', label: 'Any time', days: 0 },
  { id: 'day', label: 'Today', days: 1 },
  { id: 'week', label: 'This week', days: 7 },
  { id: 'month', label: 'This month', days: 31 }
] as const;

/** Where a message lives, as a hash route. */
function routeOf(m: Message): string | null {
  if (m.scope === 'party') return 'party/' + m.channelId;
  if (m.scope === 'crew') return 'crews';
  if (m.scope === 'group') return 'chat/g/' + m.channelId.replace(/^gc:/, '');
  if (m.scope === 'dm') return m.user ? 'chat/' + m.user.username : 'chat';
  return null;
}

export function MessageFinder({ onClose, start = 'search', channelId, channelName }: {
  onClose: () => void;
  start?: Tab;
  /** Set when opened from a conversation: search that one first. */
  channelId?: string;
  channelName?: string;
}) {
  const [tab, setTab] = useState<Tab>(start);
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  const [has, setHas] = useState<Has>('');
  const [when, setWhen] = useState<string>('');
  const [hereOnly, setHereOnly] = useState(!!channelId);
  const me = useSession((s) => s.me);
  const queryClient = useQueryClient();
  const toast = useToast();

  useEffect(() => {
    const id = window.setTimeout(() => setQ(typed.trim()), 240);
    return () => window.clearTimeout(id);
  }, [typed]);

  const after = (() => {
    const row = WHEN.find((w) => w.id === when);
    return row && row.days ? Date.now() - row.days * 24 * 60 * 60 * 1000 : 0;
  })();

  const search = useQuery({
    queryKey: ['message-search', q, has, after, hereOnly ? channelId : ''],
    queryFn: () => {
      const p = new URLSearchParams();
      if (q) p.set('q', q);
      if (has) p.set('has', has);
      if (after) p.set('after', String(after));
      if (hereOnly && channelId) p.set('channelId', channelId);
      return api.get<{ messages: Message[] }>('/api/messages/search?' + p.toString());
    },
    enabled: tab === 'search' && (q.length >= 2 || !!has),
    staleTime: 20_000
  });

  const saved = useQuery({
    queryKey: ['saved'],
    queryFn: () => api.get<{ saved: { savedAt: number; message: Message }[] }>('/api/saved'),
    enabled: tab === 'saved' && !!me
  });

  async function unsave(id: string) {
    try {
      await api.post('/api/messages/' + id + '/save', { saved: false });
      void queryClient.invalidateQueries({ queryKey: ['saved'] });
      toast('Removed from saved', 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  const rows = tab === 'search'
    ? (search.data?.messages ?? []).map((m) => ({ m, savedAt: 0 }))
    : (saved.data?.saved ?? []).map((r) => ({ m: r.message, savedAt: r.savedAt }));

  const searching = tab === 'search' && search.isFetching;
  const asked = q.length >= 2 || !!has;

  return (
    <Modal
      wide
      title={tab === 'search' ? 'Find a message' : 'Saved messages'}
      subtitle={tab === 'search'
        ? 'Search everything you can read - parties, crews, groups and direct messages.'
        : 'Only you can see these. Nobody is told you kept their message.'}
      onClose={onClose}
      footer={<Button kind="outline" onClick={onClose}>Close</Button>}
    >
      <div className="flex flex-col gap-3">
        {/* ---- which half ---- */}
        <div role="tablist" aria-label="Messages" className="flex gap-1.5">
          {([['search', 'Search'], ['saved', 'Saved']] as const).map(([id, label]) => {
            const on = tab === id;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => setTab(id)}
                className={cn(
                  'h-9 rounded-full px-4 text-[13px] font-semibold outline-none transition-colors focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                  on ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-dim hover:bg-surface-3 hover:text-ink'
                )}
              >
                {label}
              </button>
            );
          })}
        </div>

        {tab === 'search' ? (
          <>
            <label className="relative block">
              <span className="sr-only">Search messages</span>
              <Icon name="search" size={17} className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-mute" />
              <input
                autoFocus
                type="search"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder="What was said…"
                spellCheck={false}
                className={cn(
                  'h-11 w-full rounded-[calc(14px*var(--radius-mul,1))] bg-surface-1 pr-3 pl-11 text-[15px] text-ink outline-none',
                  'shadow-[inset_0_0_0_1px_var(--line)] transition-shadow placeholder:text-ghost',
                  'focus:shadow-[inset_0_0_0_1px_var(--accent),0_0_0_3px_var(--accent-soft)]'
                )}
              />
              {searching ? (
                <span aria-hidden className="absolute top-1/2 right-4 h-4 w-4 -translate-y-1/2 animate-spin rounded-full border-2 border-line border-t-accent" />
              ) : null}
            </label>

            <div className="flex flex-wrap items-center gap-1.5">
              {HAS.map((h) => {
                const on = has === h.id;
                return (
                  <button
                    key={h.id || 'any'}
                    type="button"
                    onClick={() => setHas(h.id)}
                    aria-pressed={on}
                    className={cn(
                      'flex h-8 items-center gap-1.5 rounded-full px-3 text-[12.5px] font-semibold outline-none transition-colors',
                      on ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-dim hover:bg-surface-3 hover:text-ink'
                    )}
                  >
                    <Icon name={h.icon} size={13} />
                    {h.label}
                  </button>
                );
              })}
              <span className="mx-1 h-5 w-px bg-line-soft" />
              {WHEN.map((w) => {
                const on = when === w.id;
                return (
                  <button
                    key={w.id || 'any'}
                    type="button"
                    onClick={() => setWhen(w.id)}
                    aria-pressed={on}
                    className={cn(
                      'h-8 rounded-full px-3 text-[12.5px] font-semibold outline-none transition-colors',
                      on ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-dim hover:bg-surface-3 hover:text-ink'
                    )}
                  >
                    {w.label}
                  </button>
                );
              })}
              {channelId ? (
                <button
                  type="button"
                  onClick={() => setHereOnly((v) => !v)}
                  aria-pressed={hereOnly}
                  className={cn(
                    'h-8 rounded-full px-3 text-[12.5px] font-semibold outline-none transition-colors',
                    hereOnly ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-dim hover:bg-surface-3 hover:text-ink'
                  )}
                >
                  {channelName ? 'In ' + channelName : 'This conversation'}
                </button>
              ) : null}
            </div>
          </>
        ) : null}

        {/* ---- results ---- */}
        {tab === 'search' && !asked ? (
          <p className="m-0 px-1 py-8 text-center text-[13.5px] text-mute">
            Type at least two letters, or pick what it carried.
          </p>
        ) : !rows.length ? (
          <p className="m-0 px-1 py-8 text-center text-[13.5px] text-mute">
            {searching ? 'Looking…' : tab === 'saved' ? 'Nothing saved yet. Use “Save this message” in a message’s menu.' : 'Nothing matches that.'}
          </p>
        ) : (
          <ul className="m-0 flex max-h-[46vh] list-none flex-col gap-1.5 overflow-y-auto p-0">
            {rows.map(({ m, savedAt }) => {
              const route = routeOf(m);
              return (
                <li key={m.id} className="rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-2.5">
                  <div className="flex items-center gap-2">
                    {m.user ? <Avatar user={m.user} size={24} /> : null}
                    <span className="truncate text-[13px] font-bold text-ink">{m.user?.displayName ?? 'Someone'}</span>
                    <span className="flex-none text-[11.5px] text-mute">{ago(m.createdAt)}</span>
                    <span className="flex-none text-[11.5px] text-ghost">
                      {m.scope === 'dm' ? 'direct message' : m.scope === 'party' ? 'party' : m.scope === 'crew' ? 'crew' : 'group'}
                    </span>
                    <span className="flex-1" />
                    {savedAt ? (
                      <Button size="sm" kind="plain" onClick={() => void unsave(m.id)}>Remove</Button>
                    ) : null}
                    {route ? (
                      <Button size="sm" kind="outline" onClick={() => { onClose(); go(route); }}>Open</Button>
                    ) : null}
                  </div>
                  <p className="m-0 mt-1.5 line-clamp-3 text-[13.5px] text-dim">
                    {m.body ? <EmojiText text={m.body} size={16} /> : <span className="text-mute">{(m.attachments ?? []).length} attachment(s)</span>}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
}
